/**
 * Tooltip
 *
 * Usage:
 *
 * {% include template_source('button') with {
 *     'tag': 'button',
 *     'text': 'Top',
 *     'attr': {
 *         'data-tooltip': true,
 *         'data-tooltip-hover-html': '<i>Tooltip?</i>',
 *         'data-tooltip-click-html': '<i>Tooltip!</i>',
 *         'data-tooltip-side': 'top'
 *     }
 * } only %}
 *
 * @class
 * @constructor
 * @public
 */
class Tooltip {
  /**
   * Constructor
   * @returns {class}
   */
  constructor({
    targetTooltip = '[data-tooltip]',
    targetDistance = 0, // px
    waitOnClick = 1000, // ms
    onShow = () => { },
    onClose = () => { }
  }) {
    // Save a reference to the passed config
    this.config = { targetTooltip, targetDistance, waitOnClick, onShow, onClose };

    // Tooltip reference
    const triggers = document.querySelectorAll(this.config.targetTooltip);

    // Register events only if pre binding eventListeners
    if (triggers.length > 0) {
      this.registerTriggers(...triggers);
    }
  }

  registerTriggers (...triggers) {
    triggers.forEach(trigger => {
      trigger.dataset.tooltipHoverHtml = trigger.dataset.tooltipHoverHtml || trigger.title || '';
      trigger.dataset.tooltipClickHtml = trigger.dataset.tooltipClickHtml || '';

      if (trigger.dataset.tooltipHoverHtml) {
        trigger.dataset.tooltipHtml = trigger.hoverHtml;

        // We don't need 'em longer
        trigger.removeAttribute('title');
        trigger.setAttribute('aria-label', trigger.dataset.tooltipHoverHtml);

        trigger.addEventListener('mouseover', this.onMouseOver.bind(this));
        trigger.addEventListener('mouseout', this.onMouseOut.bind(this));
      }

      if (trigger.dataset.tooltipClickHtml) {
        // Bind function for event listeners
        trigger.addEventListener('click', this.onClick.bind(this));
      }

      if (!(trigger.dataset.tooltipClickHtml || trigger.dataset.tooltipHoverHtml)) {
        trigger.addEventListener('showTooltip', this.onEvent.bind(this));
      }
    });
  }

  onEvent(event) {
    const that = this;
    that.activeElement = event.target;

    if (event.detail) {
      this.activeElement.dataset.tooltipHtml = event.detail;
      that.showTooltip();
      that.waitCloseTooltip( 500 );
    } else {
      console.error('Tooltip triggered by Event without tooltip-data');
    }
  }

  onClick(event) {
    const that = this;
    that.activeElement = event.target;

    if (this.activeElement.dataset.tooltipClickHtml) {
      this.activeElement.dataset.tooltipHtml = this.activeElement.dataset.tooltipClickHtml;
    }

    that.showTooltip();

    // Tooltip timeout (waiting)
    if (that.config.waitOnClick) {
      that.isWaiting = true;

      that.tooltipTimeout = setTimeout(() => {
        that.isWaiting = false;
        that.closeTooltip();
      }, that.config.waitOnClick);
    }
  }

  onMouseOver(event) {
    this.activeElement = event.target;

    if (this.activeElement.dataset.tooltipHoverHtml) {
      this.activeElement.dataset.tooltipHtml = this.activeElement.dataset.tooltipHoverHtml;
    } else {
      return;
    }

    this.showTooltip();
  }

  onMouseOut(event) {
    if (this.isWaiting) {
      return;
    }
    this.closeTooltip();
  }

  showTooltip() {
    this.clearTooltip();
    if (this.activeElement.hasAttribute('data-tooltip')) {
      this.renderTooltip();
    }

    // Trigger external method
    this.config.onShow(this.activeElement);
  }

  closeTooltip() {
    this.clearTooltip();

    // Trigger external method
    this.config.onShow(this.activeElement);
  }

  waitCloseTooltip() {
    // Tooltip timeout (waiting)
    if (this.config.waitOnClick) {
      this.isWaiting = true;

      this.tooltipTimeout = setTimeout(() => {
        this.isWaiting = false;
        this.closeTooltip();
      }, this.config.waitOnClick);
    }
  }

  clearTooltip() {
    clearTimeout(this.tooltipTimeout);
    try{
      document.querySelector('.tooltip').remove();
    } catch(event) {}
  }

  renderTooltip() {
    let tooltipHtml;
    const tip = this.activeElement.dataset.tooltipHtml;

    if (tip === '') {
      return;
    }

    try {
      tooltipHtml = this.activeElement.hasAttribute('data-tooltip-hover-html') ? tip : document.querySelector(tip).outerHTML;
    } catch(event) {
      tooltipHtml = tip;
    }

    tooltipHtml = tooltipHtml.replace(/<script/g, '&lt;script').replace(/<\/script/g, '&lt;\/script');
    document.body.insertAdjacentHTML('beforeend', `<div class="tooltip" role="tooltip">${tooltipHtml}</div>`);

    if (this.activeElement.hasAttribute('data-tooltip-class')) {
      document.querySelector('.tooltip').classList.add(this.activeElement.dataset.tooltipClass);
    }

    this.handleSide();
  }

  handleSide() {
    const tooltip = document.querySelector('.tooltip');
    const rect = this.activeElement.getBoundingClientRect();
    const top = window.scrollY;
    const left = window.scrollX;
    const side = this.activeElement.dataset.tooltipSide;
    const position = {
      left: () => tooltip.setAttribute('style', `left: ${rect.x - tooltip.clientWidth + left - this.config.targetDistance}px;top: ${rect.y + (rect.height - tooltip.clientHeight)/2 + top}px;`),
      right: () => tooltip.setAttribute('style', `left: ${rect.x + rect.width + left + this.config.targetDistance}px;top: ${rect.y + (rect.height - tooltip.clientHeight)/2 + top}px;`),
      top: () => tooltip.setAttribute('style', `left: ${rect.x + (rect.width - tooltip.clientWidth)/2 + left}px;top: ${rect.y - tooltip.clientHeight + top - this.config.targetDistance}px;`),
      bottom: () => tooltip.setAttribute('style', `left: ${rect.x + (rect.width - tooltip.clientWidth)/2 + left}px;top: ${rect.y + rect.height + top + this.config.targetDistance}px;`)
    }

    position[side]();
    tooltip.classList.add(`tooltip--side-${side}`);
  }

  /**
   * Class name
   * @returns {string} The human readable class name
   */
  get name() {
    return 'Tooltip';
  }
};

module.exports = Tooltip;
